import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLearners, createLearner, deleteLearner, updateLearnerStatus, patchLearner } from '../../../store/learnersSlice';
import { fetchICTData } from '../../../store/uiSlice';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../../Styles/Common.css';
const user = JSON.parse(localStorage.getItem("user"));
const userRole = user?.role || null;
const isReadOnly = userRole === "sales";

const Learners = () => {
    const dispatch = useDispatch();
    const { learners, loading, error } = useSelector((state) => state.learners);
    const { ictData } = useSelector((state) => state.ui);

    const [currentPage, setCurrentPage] = useState(1);
    const [isPopupOpen, setPopupOpen] = useState(false);
    const [isCreatePopup, setCreatePopup] = useState(false); // Differentiates between create and update
    const [learnerForm, setLearnerForm] = useState({
        firstName: '',
        lastName: '',
        username: '',
        contact: '',
        email: '',
        orgName: '',
        countryName: '',
        status: 'A',
        trainingId: '',
        ictId_id: '',
        password: '', // Required for create
    });

    const ITEMS_PER_PAGE = 10;

    useEffect(() => {
        dispatch(fetchLearners());
        dispatch(fetchICTData());
    }, [dispatch]);

    const handleOpenCreatePopup = () => {
        setCreatePopup(true);
        setPopupOpen(true);
        setLearnerForm({
            firstName: '',
            lastName: '',
            username: '',
            contact: '',
            email: '',
            orgName: '',
            countryName: '',
            status: 'A',
            trainingId: '',
            ictId_id: '',
            password: '',
        });
    };

    const handleOpenUpdatePopup = (learner) => {
        setCreatePopup(false);
        setPopupOpen(true);
        setLearnerForm({
            ...learner,
            password: '', // Password is not required for updates
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...learnerForm,
                ictId_id: parseInt(learnerForm.ictId_id, 10),
                trainingId: parseInt(learnerForm.trainingId, 10),
            };

            if (isCreatePopup) {
                // Validation for create operation
                if (!learnerForm.password) {
                    toast.error('Password is required for new learners.');
                    return;
                }
                await dispatch(createLearner(payload)).unwrap();
                toast.success('Learner created successfully!');
            } else {
                // Validation for update operation
                delete payload.password; // Remove password for updates
                await dispatch(patchLearner({ ...payload, id: learnerForm.id })).unwrap();
                toast.success('Learner updated successfully!');
            }

            setPopupOpen(false);
            dispatch(fetchLearners());
        } catch (error) {
            toast.error(error || 'Failed to process learner request');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this learner?')) {
            try {
                await dispatch(deleteLearner(id)).unwrap();
                toast.success('Learner deleted successfully!');
                dispatch(fetchLearners());
            } catch (error) {
                toast.error(error || 'Failed to delete learner');
            }
        }
    };

    const handleStatusToggle = async (id, currentStatus) => {
        try {
            const newStatus = currentStatus === 'A' ? 'I' : 'A';
            await dispatch(updateLearnerStatus({ id, status: newStatus })).unwrap();
            toast.success('Status updated successfully!');
            dispatch(fetchLearners());
        } catch (error) {
            toast.error(error || 'Failed to update status');
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const paginatedLearners = learners.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    return (
        <div className="container">
            <div className='header-container'>
                <h3>Learners Management</h3>
                <button className="add-lead-btn" onClick={handleOpenCreatePopup}>
                    <i className="fa fa-plus"></i>
                </button>
            </div>
            <table className="table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Full Name</th>
                        <th>Username</th>
                        <th>Contact</th>
                        <th>Organization</th>
                        <th>Country</th>
                        <th>Training ID</th>
                        <th>ICT Name</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {paginatedLearners.map((learner, index) => (
                        <tr key={learner.id}>
                            <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                            <td>{`${learner.firstName || ''} ${learner.lastName || ''}`}</td>
                            <td>{learner.username}</td>
                            <td>{learner.contact}</td>
                            <td>{learner.orgName}</td>
                            <td>{learner.countryName}</td>
                            <td>{learner.trainingId}</td>
                            <td>{ictData.find((ict) => ict.id === learner.ictId_id)?.ictName || 'N/A'}</td>
                            <td>
                                <i
                                    className="fa fa-edit action-icon editIcon"
                                    onClick={() => handleOpenUpdatePopup(learner)}
                                ></i>
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={learner.status === 'A'}
                                        onChange={() => handleStatusToggle(learner.id, learner.status)}
                                    />
                                    <span className="slider round"></span>
                                </label>
                                <i
                                    className="fa fa-trash action-icon deleteIcon"
                                    onClick={() => handleDelete(learner.id)}
                                ></i>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="pagination">
                {Array.from({ length: Math.ceil(learners.length / ITEMS_PER_PAGE) }).map((_, index) => (
                    <button
                        key={index}
                        className={`pagination-btn ${currentPage === index + 1 ? 'active' : ''}`}
                        onClick={() => handlePageChange(index + 1)}
                    >
                        {index + 1}
                    </button>
                ))}
            </div>
            {isPopupOpen && (
                <div className="popup-overlay">
                    <div className="popup-content">
                        <h3>{isCreatePopup ? 'Add Learner' : 'Update Learner'}</h3>
                        <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <input
                                type="text"
                                placeholder="First Name"
                                value={learnerForm.firstName}
                                onChange={(e) =>
                                    setLearnerForm({ ...learnerForm, firstName: e.target.value })
                                }
                            />
                        </div>
                        <div className="form-group">
                            <input
                                type="text"
                                placeholder="Last Name"
                                value={learnerForm.lastName}
                                onChange={(e) =>
                                    setLearnerForm({ ...learnerForm, lastName: e.target.value })
                                }
                            />
                        </div>
                        <div className="form-group">
                            <input
                                type="text"
                                placeholder="Username"
                                value={learnerForm.username}
                                onChange={(e) =>
                                    setLearnerForm({ ...learnerForm, username: e.target.value })
                                }
                                required
                                />
                        </div>
                        <div className="form-group">
                            <input
                                type="text"
                                placeholder="Contact"
                                value={learnerForm.contact}
                                onChange={(e) =>
                                    setLearnerForm({ ...learnerForm, contact: e.target.value })
                                }
                                />
                        </div>
                        <div className="form-group">
                            <input
                                type="email"
                                placeholder="Email"
                                value={learnerForm.email}
                                onChange={(e) =>
                                    setLearnerForm({ ...learnerForm, email: e.target.value })
                                }
                                required
                                />
                        </div>
                        <div className="form-group">
                            <input
                                type="text"
                                placeholder="Organization Name"
                                value={learnerForm.orgName}
                                onChange={(e) =>
                                    setLearnerForm({ ...learnerForm, orgName: e.target.value })
                                }
                                required
                                />
                        </div>
                        <div className="form-group">
                            <input
                                type="text"
                                placeholder="Country"
                                value={learnerForm.countryName}
                                onChange={(e) =>
                                    setLearnerForm({ ...learnerForm, countryName: e.target.value })
                                }
                                required
                                />
                        </div>
                        <div className="form-group">
                            <input
                                type="number"
                                placeholder="Training ID"
                                value={learnerForm.trainingId}
                                onChange={(e) =>
                                    setLearnerForm({ ...learnerForm, trainingId: e.target.value })
                                }
                                required
                                />
                        </div>
                        <div className="form-group">
                            <select
                                value={learnerForm.ictId_id}
                                onChange={(e) =>
                                    setLearnerForm({ ...learnerForm, ictId_id: e.target.value })
                                }
                                required
                                >
                                <option value="">Select ICT</option>
                                {ictData.map((ict) => (
                                    <option key={ict.id} value={ict.id}>
                                        {ict.ictName}
                                    </option>
                                ))}
                            </select>
                        </div>
                            {isCreatePopup && (
                            <div className="form-group">
                                <input
                                type="password"
                                placeholder="Password"
                                value={learnerForm.password}
                                onChange={(e) =>
                                    setLearnerForm({ ...learnerForm, password: e.target.value })
                                }
                                required
                                />
                            </div>
                            )}
                            <button type="submit" className="submit-btn">
                                {isCreatePopup 
                                ? 
                                <i className='fa fa-check-square'></i> 
                                :
                                <i className='fa fa-edit'></i>}
                            </button>
                            <button className="cancel-btn fa fa-close" onClick={() => setPopupOpen(false)}>
                            </button>
                        </form>
                    </div>
                </div>
            )}
            <ToastContainer position="bottom-right" autoClose={3000} />
        </div>
    );
};

export default Learners;
